import Cookies from 'js-cookie'

import { showMessage } from './messages.js'

/**
 * Share buttons carry their spoiler-free text in `data-share-text` (see
 * GameSession.share_text). The finished modal is swapped in by htmx, so
 * listen on the document rather than binding to the buttons directly.
 *
 * Touch devices get the native share sheet; everything else copies to the
 * clipboard and confirms with a toast. Desktop browsers also implement
 * navigator.share, but their share sheets are a poor fit for pasting into a
 * chat, so they're excluded.
 */
const useShareSheet = (text) =>
    window.matchMedia('(pointer: coarse)').matches &&
    navigator.canShare?.({ text })

/**
 * Logs the press and what came of it for the dashboard (see
 * analytics.views.ShareCreate). keepalive lets it finish if the player
 * leaves the page, and failures are ignored as they don't affect sharing.
 */
const logShare = (button, method, outcome) => {
    const { logUrl, gameSession } = button.dataset
    if (!logUrl || !gameSession) return

    fetch(logUrl, {
        method: 'POST',
        headers: { 'X-CSRFToken': Cookies.get('csrftoken') },
        body: new URLSearchParams({
            game_session: gameSession,
            method,
            outcome,
        }),
        keepalive: true,
    }).catch(() => {})
}

// Each returns the outcome to log
const shareWithSheet = async (text) => {
    try {
        await navigator.share({ text })
        return 'shared'
    } catch (error) {
        // The user closing the share sheet isn't a failure.
        if (error.name === 'AbortError') return 'cancelled'
        showMessage("Couldn't share - try again")
        return 'failed'
    }
}

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text)
        showMessage('Copied to clipboard!')
        return 'shared'
    } catch {
        showMessage("Couldn't copy - try again")
        return 'failed'
    }
}

document.addEventListener('click', async (event) => {
    const button = event.target.closest('.share-button')
    if (!button) return

    const text = button.dataset.shareText
    if (!text) return

    // No clipboard fallback after a failed share: the await has used up the
    // click's user activation, so the clipboard write would be refused too.
    if (useShareSheet(text)) {
        logShare(button, 'share_sheet', await shareWithSheet(text))
    } else {
        logShare(button, 'clipboard', await copyToClipboard(text))
    }
})
